import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useDataScope } from "@/hooks/useDataScope";
import { BillingPreview } from "@/components/AdminBilling";
import { formatCurrency } from "@/lib/format";
import { Receipt, Layers, Loader2, Info, CircleDot, CheckCircle2, FileText, Download } from "lucide-react";
import { toast } from "sonner";

const CURRENT_MONTH = new Date().toISOString().slice(0, 7);

export default function Billing() {
  const { param, allMode } = useDataScope();
  const [month, setMonth] = useState(CURRENT_MONTH);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (allMode) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    const [y, m] = month.split("-");
    api.get(`/billing?year=${y}&month=${m}${param ? `&${param}` : ""}`)
      .then(({ data: d }) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [month, param, allMode]);

  const summary = data?.summary;
  const status = data?.status;
  const [downloading, setDownloading] = useState(null);

  // Blob download: the auth cookie can't ride on a cross-domain link, so fetch via axios.
  const downloadStatement = async (fmt) => {
    setDownloading(fmt);
    const [y, m] = month.split("-");
    try {
      const res = await api.get(`/billing/statement.${fmt}?year=${y}&month=${m}${param ? `&${param}` : ""}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `BoxHero-statement-${month}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not build your statement — please try again.");
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div data-testid="billing-page" className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>Billing</h1>
          <p className="text-slate-500 mt-1">Your full shipping &amp; fulfillment bill, by calendar month (ship date).</p>
        </div>
        {!allMode && (
          <div className="flex items-end gap-3">
            <div>
              <label className="block text-xs uppercase tracking-wider font-medium text-slate-500 mb-1.5">Billing month</label>
              <input
                data-testid="billing-month-input"
                type="month"
                value={month}
                max={CURRENT_MONTH}
                onChange={(e) => setMonth(e.target.value)}
                className="px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500"
              />
            </div>
            {summary && (
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-2">
                <button
                  data-testid="download-statement-pdf"
                  onClick={() => downloadStatement("pdf")}
                  disabled={downloading === "pdf"}
                  className="inline-flex items-center gap-2 bg-bh-blue hover:opacity-90 disabled:opacity-60 text-white font-semibold px-3.5 py-2 rounded-md text-sm transition-opacity"
                >
                  {downloading === "pdf" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} Statement PDF
                </button>
                <button
                  data-testid="download-statement-xlsx"
                  onClick={() => downloadStatement("xlsx")}
                  disabled={downloading === "xlsx"}
                  className="inline-flex items-center gap-2 border border-slate-300 hover:bg-slate-50 disabled:opacity-60 text-slate-800 font-semibold px-3.5 py-2 rounded-md text-sm transition-colors"
                >
                  {downloading === "xlsx" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Excel — full detail
                </button>
                </div>
                <p className="text-xs text-slate-400" data-testid="statement-hint">
                  The Excel file has three tabs: Summary, every Shipment we billed, and Credits &amp; Adjustments.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {allMode ? (
        <div data-testid="billing-all-mode-prompt" className="flex items-start gap-3 bg-blue-50 border border-blue-200 text-blue-800 rounded-lg px-5 py-6">
          <Layers className="w-5 h-5 mt-0.5 shrink-0" />
          <div className="text-sm">
            <strong>Select a specific store to view its bill.</strong>
            <p className="mt-1 text-blue-700">Billing is per store, so it isn&apos;t aggregated in the &quot;All accounts&quot; view. Pick a client from the account switcher (top-right).</p>
          </div>
        </div>
      ) : loading ? (
        <div className="flex items-center justify-center gap-2 text-slate-400 py-20">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading your bill…
        </div>
      ) : !data?.enabled ? (
        <div data-testid="billing-not-enabled" className="flex items-start gap-3 bg-slate-50 border border-slate-200 text-slate-600 rounded-lg px-5 py-6">
          <Info className="w-5 h-5 mt-0.5 shrink-0" />
          <p className="text-sm">Billing isn&apos;t configured for your account yet. Your BoxHero rep will set this up shortly.</p>
        </div>
      ) : data?.store_linked === false ? (
        <div data-testid="billing-store-not-linked" className="flex items-start gap-3 bg-amber-50 border border-amber-200 text-amber-900 rounded-lg px-5 py-6">
          <Info className="w-5 h-5 mt-0.5 shrink-0" />
          <div className="text-sm">
            <strong>Your store isn&apos;t linked to our shipping system yet.</strong>
            <p className="mt-1">Once BoxHero links it, every shipment and fee for the month will appear here. Nothing is being billed in the meantime.</p>
          </div>
        </div>
      ) : (
        <>
          {/* Status + headline total */}
          <div className="bg-white border border-slate-200 rounded-xl p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center">
                <Receipt className="w-6 h-6" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>{summary?.month_label}</h2>
                  <StatusPill status={status} />
                </div>
                <p className="text-xs text-slate-500 mt-0.5">
                  {summary?.order_count} orders shipped · {summary?.units_total} units
                  {status === "running" && " · still accumulating this month"}
                  {data?.locked && " · finalised by BoxHero"}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-wider text-slate-400">To be billed</p>
              <p data-testid="billing-to-be-billed" className="text-3xl font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>
                {formatCurrency(summary?.to_be_billed || 0)}
              </p>
            </div>
          </div>

          {summary && summary.order_count === 0 ? (
            <div className="text-center text-sm text-slate-400 py-10 bg-white border border-slate-200 rounded-xl">
              Nothing shipped in {summary.month_label} yet.
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <BillingPreview preview={data} />
            </div>
          )}

          <p className="text-xs text-slate-400 flex items-center gap-1.5">
            <Info className="w-3.5 h-3.5" />
            We bill on a full calendar-month basis — every order shipped within the month, regardless of when it was placed.
            {status === "running" && " This month is still running, so the total will keep updating until month-end."}
          </p>
        </>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  if (status === "running") {
    return (
      <span data-testid="billing-status-running" className="inline-flex items-center gap-1 text-xs font-semibold bg-blue-100 text-blue-700 px-2.5 py-1 rounded-full">
        <CircleDot className="w-3 h-3" /> Running
      </span>
    );
  }
  return (
    <span data-testid="billing-status-final" className="inline-flex items-center gap-1 text-xs font-semibold bg-green-100 text-green-700 px-2.5 py-1 rounded-full">
      <CheckCircle2 className="w-3 h-3" /> Final
    </span>
  );
}
